function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import AriaModal from 'react-aria-modal';
import { ClassNames } from '@emotion/core';
import { AlertIcon } from '@domain-group/fe-co-icon';
import { listingVisibility, ListingCategory, ListingType, ListingTypeName, LocalityType, RentalType, PropertyType } from '@domain-group/fe-allhomes-library';
import { SearchQuery } from '@domain-group/fe-allhomes-search-api';
import AllhomesBreadcrumbs from '@domain-group/fe-co-allhomes-breadcrumbs';
import MobileDialog from '@domain-group/fe-co-allhomes-mobile-dialog';
import AllhomesSearchFeaturedProperty from '@domain-group/fe-co-allhomes-search-featured-property';
import AllhomesSearchFilter, { AllhomesSearchFilterMobile } from '@domain-group/fe-co-allhomes-search-filter';
import { buildSearchTitlePrefix } from '@domain-group/fe-allhomes-search-api/lib/js/utils/build-search-title-prefix';
import Separator from '../components/separator';
import ViewToggle from '../components/allhomes-listing-view-toggle';
import Footer from '../components/allhomes-listing-search-footer';
import Spinner from '../components/spinner';
import RecentlySoldLink from '../components/allhomes-listing-recently-sold-link';
import { ListSearchResults, ListSearchResultsSold } from '../components/list-search-results';
import MapSearch from '../components/allhomes-listing-map-search';
import SearchLocation from '../components/search-location';
import MessageBox from '../components/confirmation/messagebox';
import SchoolsDisclaimer from '../components/schools-disclaimer';
import PropertyAlertButton from '../components/property-alert-button';
import AppraisalCta from '../components/appraisal-cta';
import { AllhomesSearchSorting, AllhomesSearchSortingMobile, AllhomesSearchSortingTablet } from '../components/sorting';
import ViewSoldLink from '../components/view-sold-link/view-sold-link';
import mapSearchPropType from '../proptypes/map-search';
import viewPropType, { VIEWS } from '../proptypes/view';
import localityPropType from '../proptypes/locality';
import { parseHtml, toggleSold } from '../utils';
import { BtrMoreResults } from '../components/btr-more-results';
import pageWrapper from '../wrappers';
import * as styles from './allhomes-listing-search-results.style';
import { jsx as ___EmotionJSX } from "@emotion/core";
var baseClass = 'allhomes-listing-search-results';
var MAP_VIEW_PARAM = 'view=map';

// Constants related to the size of the More Options dropdown window, which must be sized so that it ends 40px from
// the bottom edge of the browser window.
var DROPDOWN_STARTING_POSITION = 365;
var DROPDOWN_BOTTOM_PADDING = 40;
var buildLabel = locality => {
  var _locality$metaData;
  if (!locality) {
    return null;
  }
  switch (locality.type) {
    case LocalityType.DIVISION.type:
      return "in ".concat(locality.name, ", ").concat(locality.metaData.state, ", ").concat(locality.metaData.postcode);
    case LocalityType.REGION.type:
    case LocalityType.DISTRICT.type:
      return "in ".concat(locality.name, ", ").concat(locality.metaData.state);
    case LocalityType.POSTCODE.type:
      return "in postcode ".concat(locality.id);
    case LocalityType.STREET.type:
      return "in ".concat(locality.name).concat(locality.parent ? ", ".concat(locality.parent.name) : '', ", ").concat(locality.metaData.state, ", ").concat(locality.metaData.postcode);
    case LocalityType.SCHOOL.type:
      return locality !== null && locality !== void 0 && (_locality$metaData = locality.metaData) !== null && _locality$metaData !== void 0 && _locality$metaData.isCatchment ? "in the ".concat(locality.name, " ").concat(locality.metaData.catchmentTerm) : "within 5km of ".concat(locality.name);
    default:
      return null;
  }
};
var populateNameFromFilters = filters => filters.getLocalities().map(location => ({
  name: location.name
}));
var getRedirectMessage = (referrer, paName) => ({
  LISTING_NOT_FOUND: "That property isn't here anymore, but here are some others in the same suburb:",
  MISSING_EA_LISTING: ___EmotionJSX("div", null, "This Early Access property isn't available any more, but here are more listings that match your property alert", ' ', ___EmotionJSX("span", {
    css: styles.redirectMessageTextPAName
  }, paName))
})[referrer];

/* eslint-disable no-param-reassign */
var fixGovCopyright = {
  // Add required attributes to <a> tags
  a: (text, attributes) => {
    attributes.target = '_blank';
    attributes.rel = 'noopener';
  }
};
/* eslint-enable no-param-reassign */

class AllhomesListingSearchResults extends React.Component {
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    _defineProperty(this, "handleFeaturedPropertyClick", (position, property) => {
      this.props.sendEvent('featured-property-click', {
        type: 'featured-property',
        position,
        property
      });
      this.props.onFeaturePropertyClick(position, property);
    });
    _defineProperty(this, "isMapView", () => isEqual(this.state.view, VIEWS.MAP));
    _defineProperty(this, "isListView", () => isEqual(this.state.view, VIEWS.LIST));
    _defineProperty(this, "filters", () => this.props.searchQuery.filters);
    _defineProperty(this, "sortOrder", () => this.props.searchQuery.getSortUrl());
    _defineProperty(this, "currentPage", () => this.props.searchQuery.page.value);
    _defineProperty(this, "updateViewUrlParam", () => {
      if (typeof document !== 'undefined') {
        var currentSearch = '';
        if (isEmpty(document.location.search)) {
          currentSearch = '?';
        } else {
          currentSearch = "".concat(document.location.search).concat(document.location.search.endsWith('&') ? '' : '&');
        }
        if (this.isMapView() && !currentSearch.includes(MAP_VIEW_PARAM)) {
          currentSearch = "".concat(currentSearch).concat(MAP_VIEW_PARAM);
        } else if (this.isListView() && currentSearch.includes(MAP_VIEW_PARAM)) {
          currentSearch = currentSearch.replace("".concat(MAP_VIEW_PARAM, "&"), '');
        }
        var newUrl = document.location.pathname + currentSearch;
        window.history.pushState({
          id: "search-results".concat(this.isMapView() ? '-with-map' : '')
        }, document.title, newUrl);
      }
    });
    _defineProperty(this, "toggleView", function () {
      var triggerCallbacks = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      var newView = null;
      if (_this.isMapView()) {
        newView = VIEWS.LIST;
        if (triggerCallbacks) {
          _this.props.onToggleView(VIEWS.LIST);
        }
      } else {
        newView = VIEWS.MAP;
        if (triggerCallbacks) {
          _this.props.onToggleView(VIEWS.MAP);
        }
      }
      _this.setState({
        view: newView
      }, () => {
        _this.updateViewUrlParam();
      });
    });
    _defineProperty(this, "visibilityPeriod", listingType => listingType.category === ListingCategory.RENT ? listingVisibility.toText(this.props.listingVisibility.rent) : listingVisibility.toText(this.props.listingVisibility.sale));
    _defineProperty(this, "resultsLabel", (listingType, isSoldPage) => isSoldPage ? "".concat(listingType.category === ListingCategory.RENT ? 'rented' : 'sold', " in the last ").concat(this.visibilityPeriod(listingType)) : listingType.titleFlavour.toLowerCase());
    _defineProperty(this, "resultsNumber", (listingType, number) => {
      var _this$filters$propert, _this$filters$propert2, _this$props$singleLoc;
      var isNewDev = listingType.name === ListingType.DEVELOPMENTS_RESIDENTIAL.name;
      var suffix = "propert".concat(number === 1 ? 'y' : 'ies');
      if (!listingType.isCommercial && ((_this$filters$propert = this.filters().propertyTypes) === null || _this$filters$propert === void 0 ? void 0 : (_this$filters$propert2 = _this$filters$propert.selected) === null || _this$filters$propert2 === void 0 ? void 0 : _this$filters$propert2.length) === 1) {
        var propType = PropertyType[this.filters().propertyTypes.selected[0]];
        suffix = number === 1 ? propType.label.toLowerCase() : propType.pluralLabel.toLowerCase();
      }
      if (listingType.isCommercial) {
        var type = listingType === ListingType.SALES_BUSINESS ? 'businesses' : "commercial ".concat(suffix);
        return "".concat(number, " ").concat(type);
      }
      if (isNewDev) {
        return "".concat(number, " New development").concat(number === 1 ? '' : 's');
      }

      // if we have single division then we use title prefix for heading
      if (((_this$props$singleLoc = this.props.singleLocality) === null || _this$props$singleLoc === void 0 ? void 0 : _this$props$singleLoc.type) === LocalityType.DIVISION.type) {
        return buildSearchTitlePrefix(this.props.searchQuery, number);
      }
      return "".concat(number, " ").concat(suffix);
    });
    _defineProperty(this, "locationsLabel", () => {
      var localityLabel = buildLabel(this.props.singleLocality);
      if (localityLabel) {
        return localityLabel;
      }
      return isEqual(this.props.localityLength, 0) ? '' : "in ".concat(this.props.localityLength, " locations");
    });
    _defineProperty(this, "getAlertTitle", () => {
      if (this.props.searchQuery.isMapSearch()) {
        return 'Map Area';
      }
      var localities = populateNameFromFilters(this.filters()) || [];
      var summary = '';
      if (localities.length) {
        summary = localities[0].name;
        if (localities.length > 1) {
          var remaining = localities.length - 1;
          var suffix = remaining === 1 ? 'location' : 'locations';
          summary += " + ".concat(remaining, " ").concat(suffix);
        }
      }
      return summary;
    });
    _defineProperty(this, "getPropertyAlertButton", () => {
      var _this$props$searchQue;
      return ___EmotionJSX(PropertyAlertButton, {
        alertId: this.props.alertId,
        filterSummary: this.props.filterSummary,
        alertName: this.props.alertName || this.getAlertTitle(),
        listingType: this.props.listingType,
        query: (_this$props$searchQue = this.props.searchQuery) === null || _this$props$searchQue === void 0 ? void 0 : _this$props$searchQue.getValues(),
        editAlertUrl: this.props.editAlertUrl,
        manageAlertUrl: this.props.manageAlertUrl,
        deleteConfirmation: this.props.deleteConfirmation,
        onAlertClick: this.props.onAlertClick,
        onCreateAlert: this.props.onCreateAlert,
        onManageAlert: this.props.onManageAlert,
        onEditAlert: this.props.onEditAlert,
        onRemoveAlert: this.props.onRemoveAlert,
        earlyAccessEnabled: this.props.earlyAccessEnabled
      });
    });
    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    _defineProperty(this, "getMobilePropertyAlertButton", () => ___EmotionJSX("div", {
      onClick: event => {
        event.preventDefault();
        event.stopPropagation();
        return false;
      },
      css: styles.mobileAlertContainer
    }, ___EmotionJSX("span", {
      css: styles.mobileAlertDivider
    }, "\xA0"), this.getPropertyAlertButton()));
    /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    _defineProperty(this, "getSoldLinkUrl", () => {
      var _toggleSold;
      return (_toggleSold = toggleSold(this.props.searchQuery)) === null || _toggleSold === void 0 ? void 0 : _toggleSold.toSearchUrl();
    });
    _defineProperty(this, "scrollToTop", () => {
      if (typeof window !== 'undefined') {
        window.scroll(0, 0);
      }
    });
    // Get listing category from listing type and title-case it
    _defineProperty(this, "getListingCategory", listingType => {
      var category = ListingType[listingType].category.toLowerCase();
      return "".concat(category[0].toUpperCase()).concat(category.substring(1));
    });
    this.state = {
      backToSearchUrl: null,
      view: props.view,
      showDisclaimerDialogue: false
    };
    props.getPageRefCallback(this);
  }
  componentDidMount() {
    this.props.sendEvent('view-search-result-page');
  }
  UNSAFE_componentWillReceiveProps(_ref) {
    var media = _ref.media;
    if (media.mobile && this.isMapView()) {
      this.toggleView(false);
    }
  }
  render() {
    var _this$props$media = this.props.media,
      desktop = _this$props$media.desktop,
      tablet = _this$props$media.tablet,
      mobile = _this$props$media.mobile;
    var searchOptions = this.props.searchOptions;
    var listingType = ListingType[this.props.listingType];
    var isBtr = this.props.listingType === ListingTypeName.RENTAL_RESIDENTIAL && this.filters().listedPropertyTypes.hasOnlyValue(RentalType.BUILD_TO_RENT.name);
    var filterProps = {
      listingType: this.props.listingType,
      filters: this.filters().getValues(),
      priceRanges: searchOptions.priceRanges,
      onSubmitFilter: this.props.onSubmitFilter,
      onCancel: () => this.props.onShowFilterDialogue(false),
      onPropertyAlertClick: this.props.onPropertyAlertClick,
      visibilityPeriod: this.visibilityPeriod(listingType)
    };
    var displayViewToggle = desktop || tablet;
    var isSchoolLocality = Boolean(this.props.singleLocality) && this.props.singleLocality.type === LocalityType.SCHOOL.type;
    var schoolMetaData = isSchoolLocality && this.props.singleLocality.metaData;
    var isCatchment = isSchoolLocality && schoolMetaData.isCatchment;
    var disclaimer = isSchoolLocality && ___EmotionJSX("div", {
      css: styles.disclaimer
    }, ___EmotionJSX("span", {
      css: styles.disclaimerLine1
    }, isCatchment && "Disclaimer: always check directly with the local school to verify ".concat(schoolMetaData.catchmentTerm, " data."), !isCatchment && "This school doesn't have an official ".concat(schoolMetaData.catchmentTerm, ", but here are some properties within a 5km radius.")), ___EmotionJSX("span", null, ___EmotionJSX("button", {
      css: styles.disclaimerButton,
      type: "button",
      onClick: () => this.setState({
        showDisclaimerDialogue: true
      })
    }, "Learn\xA0more")));
    return ___EmotionJSX("section", null, ___EmotionJSX("div", {
      css: styles.topContent
    }, ___EmotionJSX(AllhomesBreadcrumbs, {
      items: this.props.breadcrumbs,
      goBackText: this.state.backToSearchUrl ? 'Back to Browse' : null,
      goBackUrl: this.state.backToSearchUrl,
      css: styles.breadcrumbs
    }), ___EmotionJSX("div", {
      css: styles.items
    }, ___EmotionJSX("div", {
      css: styles.filters
    }, ___EmotionJSX("div", {
      css: styles.location
    }, ___EmotionJSX(SearchLocation, {
      filters: this.filters(),
      onSearch: this.props.onSubmitFilter,
      onLocationEntryClick: this.props.onLocationEntryClick,
      propertyAlertButton: this.getPropertyAlertButton(),
      mobilePropertyAlertButton: this.getMobilePropertyAlertButton()
    })), (desktop || tablet) && ___EmotionJSX(AllhomesSearchFilter, _extends({
      css: styles.searchFilter
    }, filterProps, {
      dropdownHeightOffset: DROPDOWN_STARTING_POSITION + DROPDOWN_BOTTOM_PADDING
    }))), ___EmotionJSX("div", {
      css: styles.summaryContainer
    }, ___EmotionJSX("h1", {
      css: styles.summary
    }, ___EmotionJSX("span", {
      css: styles.summaryStat
    }, this.resultsNumber(listingType, this.props.totalHits)), ' ', this.resultsLabel(listingType, this.props.isSoldPage), ' ', ___EmotionJSX("span", null, this.locationsLabel())), ___EmotionJSX("div", {
      css: styles.disclaimerMobileContainer
    }, isSchoolLocality && disclaimer), ___EmotionJSX("div", {
      css: styles.controls
    }, displayViewToggle && ___EmotionJSX(ViewToggle, {
      type: this.isListView() ? VIEWS.MAP : VIEWS.LIST,
      onToggle: this.toggleView
    }), displayViewToggle && ___EmotionJSX(Separator, null), (desktop || tablet) && this.props.listingType !== ListingType.SHARE_RESIDENTIAL.name && ___EmotionJSX(RecentlySoldLink, {
      listingType: this.props.listingType,
      isSoldPage: this.props.isSoldPage,
      linkUrl: this.getSoldLinkUrl(),
      onClick: this.props.onViewSoldClick
    }), (desktop || tablet) && this.isListView() && this.props.listingType !== ListingType.SHARE_RESIDENTIAL.name && ___EmotionJSX(Separator, null), desktop && this.isListView() && ___EmotionJSX(AllhomesSearchSorting, {
      listingType: this.props.listingType,
      value: this.sortOrder(),
      onSelect: this.props.onSortChange
    }), tablet && this.isListView() && ___EmotionJSX(AllhomesSearchSortingTablet, {
      align: "right",
      listingType: this.props.listingType,
      value: this.sortOrder(),
      onSelect: this.props.onSortChange
    }), mobile && ___EmotionJSX("div", {
      css: styles.sortControls
    }, this.props.listingType !== ListingType.SHARE_RESIDENTIAL.name && ___EmotionJSX(RecentlySoldLink, {
      listingType: this.props.listingType,
      isSoldPage: this.props.isSoldPage,
      linkUrl: this.getSoldLinkUrl(),
      isMobile: true,
      onClick: this.props.onViewSoldClick
    }), ___EmotionJSX("div", {
      css: styles.sortSpacer
    }), ___EmotionJSX("button", {
      type: "button",
      css: styles.button,
      onClick: () => this.props.onShowFilterDialogue(true)
    }, "Filters"), ___EmotionJSX("button", {
      type: "button",
      css: styles.button,
      onClick: () => this.props.onShowSortDialogue(true)
    }, "Sort")))), ___EmotionJSX("div", {
      css: styles.disclaimerNonMobileContainer
    }, isSchoolLocality && disclaimer), this.props.redirectMessage && ___EmotionJSX("div", {
      css: styles.redirectMessageBox
    }, ___EmotionJSX("div", {
      css: styles.redirectMessageIcon
    }, ___EmotionJSX(AlertIcon, null)), ___EmotionJSX("div", {
      css: styles.redirectMessageText
    }, getRedirectMessage(this.props.redirectMessage, this.props.propertyAlertName))), this.props.displayMessage && ___EmotionJSX(MessageBox, {
      type: this.props.messageType,
      onClose: this.props.closeMessage,
      onUndo: this.props.undoDelete
    }), this.isListView() && ___EmotionJSX("div", {
      css: styles.featuredProperty
    }, this.props.searchPending && ___EmotionJSX(Spinner, null), !isEmpty(this.props.featuredProperties) && !isBtr && ___EmotionJSX("div", {
      css: styles.marginContainer({
        showDots: !mobile || this.props.featuredProperties.length > 1
      })
    }, ___EmotionJSX(AllhomesSearchFeaturedProperty, {
      key: this.props.featuredProperties.map(each => "".concat(each.listing.id)).join(),
      listingType: this.props.listingType,
      listings: this.props.featuredProperties,
      onAddToWatchlist: this.props.onAddToWatchlistForListView,
      onFeaturePropertyClick: this.handleFeaturedPropertyClick
    }))))), ___EmotionJSX("div", {
      css: styles.itemsContainer
    }, this.isListView() && this.props.searchPending && ___EmotionJSX(Spinner.Overlay, null), this.isListView() && (this.props.searchQuery.isSold() ? ___EmotionJSX(ListSearchResultsSold, {
      searchResults: this.props.searchResults,
      media: this.props.media,
      featuredProperties: this.props.featuredProperties,
      propertiesOfTheWeek: this.props.propertiesOfTheWeek,
      onInlineAdClick: this.props.onInlineAdClick,
      inlineAds: this.props.inlineAds,
      inlineAdsDisabled: this.props.inlineAdsDisabled,
      isDefaultSearch: this.props.isDefaultSearch,
      onClearFilter: this.props.onClearFilter,
      onPropertyAlertClick: this.props.onPropertyAlertClick,
      currentPage: this.currentPage(),
      totalPages: this.props.totalPages,
      paginatorUrlTemplate: this.props.paginatorUrlTemplate,
      onPageChange: this.props.onPageChange,
      onAddToWatchlist: this.props.onAddToWatchlistForListView,
      enquiryEndpoint: this.props.endpoints.enquiryEndpoint,
      filters: this.filters()
    }) : ___EmotionJSX(ListSearchResults, {
      searchResults: this.props.searchResults,
      media: this.props.media,
      featuredProperties: this.props.featuredProperties,
      propertiesOfTheWeek: this.props.propertiesOfTheWeek,
      onInlineAdClick: this.props.onInlineAdClick,
      inlineAds: this.props.inlineAds,
      inlineAdsDisabled: this.props.inlineAdsDisabled,
      isDefaultSearch: this.props.isDefaultSearch,
      onClearFilter: this.props.onClearFilter,
      onPropertyAlertClick: this.props.onPropertyAlertClick,
      currentPage: this.currentPage(),
      totalPages: this.props.totalPages,
      paginatorUrlTemplate: this.props.paginatorUrlTemplate,
      onPageChange: this.props.onPageChange,
      onAddToWatchlist: this.props.onAddToWatchlistForListView,
      filters: this.filters()
    })), this.isListView() && isBtr && ___EmotionJSX(BtrMoreResults, _extends({}, this.props.btrMoreResults, {
      onAddToWatchlist: this.props.onAddToWatchlistForListView,
      onExpandedBtrSearch: newLocalities => {
        this.props.onExpandedBtrSearch(newLocalities);
        this.scrollToTop();
      },
      onClearBtr: () => {
        this.props.onClearBtr();
        this.scrollToTop();
      },
      primarySearchResult: {
        totalHits: this.props.totalHits,
        listingIds: this.props.searchResults.map(result => {
          var _result$listing;
          return result === null || result === void 0 ? void 0 : (_result$listing = result.listing) === null || _result$listing === void 0 ? void 0 : _result$listing.id;
        }).filter(Boolean)
      },
      isExpandedSearch: true,
      listingType: this.props.listingType,
      buildSearchApi: this.props.buildSearchApi,
      filters: this.filters()
    })), this.isMapView() && this.props.map && ___EmotionJSX(MapSearch, _extends({}, this.props.map, {
      baseClass: baseClass,
      onMapMove: this.props.onMapMove,
      onMapZoom: this.props.onMapZoom,
      onMarkerClick: this.props.onMarkerClick,
      onAddToWatchlist: this.props.onAddToWatchlistForMapView,
      watchlist: this.props.watchlist,
      listingCategory: this.getListingCategory(this.props.listingType)
    })), !this.props.isSoldPage && this.props.listingType !== ListingType.SHARE_RESIDENTIAL.name && ___EmotionJSX(ViewSoldLink, {
      titleText: "Looking for recently ".concat(ListingType[this.props.listingType].pastParticiple, " properties?"),
      detailText: "",
      onClick: () => {
        this.props.onViewSoldClick();
        this.scrollToTop();
      }
    }), ___EmotionJSX(Footer, {
      singleLocality: this.props.singleLocality,
      hostName: this.props.hostName,
      numberOfProperties: this.props.searchResults.length,
      surroundingSuburbs: this.props.surroundingSuburbs,
      media: this.props.media,
      onPropertyAlertClick: this.props.onPropertyAlertClick,
      listingCategory: this.getListingCategory(this.props.listingType)
    })), this.props.showSortDialogue && ___EmotionJSX(MobileDialog, {
      title: "Sort by",
      closeButton: true,
      onClose: () => this.props.onShowSortDialogue(false)
    }, ___EmotionJSX(AllhomesSearchSortingMobile, {
      listingType: this.props.listingType,
      value: this.sortOrder(),
      onSelect: selection => {
        this.props.onSortChange(selection);
      }
    })), this.props.showFilterDialogue && ___EmotionJSX(ClassNames, null, _ref2 => {
      var css = _ref2.css;
      return ___EmotionJSX(AriaModal, {
        includeDefaultStyles: false,
        underlayClass: css(styles.ariaUnderlay),
        dialogClass: css(styles.ariaDialog),
        titleText: "Filters"
      }, ___EmotionJSX(AllhomesSearchFilterMobile, filterProps));
    }), ___EmotionJSX(SchoolsDisclaimer, {
      show: this.state.showDisclaimerDialogue,
      onClose: () => this.setState({
        showDisclaimerDialogue: false
      }),
      governmentCopyright: isSchoolLocality ? parseHtml(schoolMetaData.governmentCopyright, fixGovCopyright) : null
    }), this.props.isSoldPage && mobile && ___EmotionJSX(AppraisalCta, {
      enquiryEndpoint: this.props.endpoints.enquiryEndpoint,
      filters: this.filters()
    }));
  }
}
AllhomesListingSearchResults.propTypes = {
  media: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types
  breadcrumbs: AllhomesBreadcrumbs.propTypes.items,
  allSuburbsLink: PropTypes.string,
  listingType: PropTypes.string.isRequired,
  searchResults: ListSearchResults.propTypes.searchResults,
  inlineAds: ListSearchResults.propTypes.inlineAds,
  inlineAdsDisabled: PropTypes.bool,
  localityLength: PropTypes.number.isRequired,
  singleLocality: localityPropType,
  isDefaultSearch: PropTypes.bool,
  isSoldPage: PropTypes.bool,
  surroundingSuburbs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    href: PropTypes.string
  })),
  featuredProperties: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types
  propertiesOfTheWeek: ListSearchResults.propTypes.propertiesOfTheWeek,
  searchOptions: PropTypes.shape({
    priceRanges: PropTypes.arrayOf(PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
      step: PropTypes.number.isRequired
    })).isRequired
  }).isRequired,
  searchPending: PropTypes.bool,
  onSubmitFilter: PropTypes.func,
  hostName: PropTypes.string,
  totalPages: PropTypes.number,
  totalHits: PropTypes.number,
  paginatorUrlTemplate: PropTypes.string.isRequired,
  showFilterDialogue: PropTypes.bool,
  showSortDialogue: PropTypes.bool,
  onPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onAddToWatchlistForListView: PropTypes.func,
  onAddToWatchlistForMapView: PropTypes.func,
  onViewSoldClick: PropTypes.func,
  onFeaturePropertyClick: PropTypes.func,
  onLocationEntryClick: PropTypes.func,
  onClearFilter: PropTypes.func,
  onClearBtr: PropTypes.func,
  onExpandedBtrSearch: PropTypes.func,
  onShowSortDialogue: PropTypes.func,
  onShowFilterDialogue: PropTypes.func,
  onAllSuburbsLinkClick: PropTypes.func,
  getPageRefCallback: PropTypes.func,
  onPropertyAlertClick: PropTypes.func,
  onInlineAdClick: PropTypes.func,
  onMapViewClick: PropTypes.func,
  onToggleView: PropTypes.func,
  onMapZoom: PropTypes.func,
  onMapMove: PropTypes.func,
  onMarkerClick: PropTypes.func,
  view: viewPropType,
  toggledFeatures: PropTypes.objectOf(PropTypes.bool),
  map: mapSearchPropType,
  watchlist: PropTypes.arrayOf(PropTypes.number),
  redirectMessage: PropTypes.string,
  propertyAlertName: PropTypes.oneOf(['LISTING_NOT_FOUND', 'MISSING_EA_LISTING']),
  editAlertUrl: PropTypes.string,
  manageAlertUrl: PropTypes.string,
  searchQuery: PropTypes.instanceOf(SearchQuery).isRequired,
  displayMessage: PropTypes.bool,
  messageType: PropTypes.string,
  undo: PropTypes.bool,
  closeMessage: PropTypes.func,
  undoDelete: PropTypes.func,
  deleteConfirmation: PropTypes.func,
  onAlertClick: PropTypes.func,
  onCreateAlert: PropTypes.func,
  onManageAlert: PropTypes.func,
  onEditAlert: PropTypes.func,
  onRemoveAlert: PropTypes.func,
  alertId: PropTypes.number,
  alertName: PropTypes.string,
  earlyAccessEnabled: PropTypes.bool,
  filterSummary: PropTypes.string,
  listingVisibility: PropTypes.shape({
    sale: PropTypes.shape({
      duration: PropTypes.number,
      unit: PropTypes.string
    }),
    rent: PropTypes.shape({
      duration: PropTypes.number,
      unit: PropTypes.string
    })
  }),
  sendEvent: PropTypes.func,
  endpoints: PropTypes.shape({
    enquiryEndpoint: PropTypes.string.isRequired
  }).isRequired,
  buildSearchApi: PropTypes.func.isRequired,
  btrMoreResults: PropTypes.shape({
    newsArticles: PropTypes.array.isRequired,
    // eslint-disable-line react/forbid-prop-types
    searchResults: PropTypes.array.isRequired,
    // eslint-disable-line react/forbid-prop-types
    totalHits: PropTypes.number.isRequired
  })
};
AllhomesListingSearchResults.defaultProps = {
  allSuburbsLink: null,
  searchPending: false,
  breadcrumbs: [],
  searchResults: [],
  btrMoreResults: null,
  singleLocality: null,
  isDefaultSearch: false,
  isSoldPage: false,
  onSubmitFilter: () => {},
  surroundingSuburbs: [],
  featuredProperties: [],
  propertiesOfTheWeek: [],
  hostName: '',
  totalHits: 0,
  showFilterDialogue: false,
  showSortDialogue: false,
  onPageChange: () => {},
  onSortChange: () => {},
  onAddToWatchlistForListView: () => {},
  onAddToWatchlistForMapView: () => {},
  onViewSoldClick: () => {},
  onFeaturePropertyClick: () => {},
  onLocationEntryClick: () => {},
  onClearFilter: () => {},
  onClearBtr: () => {},
  onExpandedBtrSearch: () => {},
  onShowSortDialogue: () => {},
  onShowFilterDialogue: () => {},
  getPageRefCallback: () => {},
  onAllSuburbsLinkClick: () => {},
  onPropertyAlertClick: () => {},
  onInlineAdClick: () => {},
  onToggleView: () => {},
  onMapViewClick: () => {},
  onMapZoom: () => {},
  onMapMove: () => {},
  onMarkerClick: () => {},
  inlineAds: {
    urls: [],
    numberOfListingsPerAd: 6
  },
  inlineAdsDisabled: false,
  totalPages: 1,
  toggledFeatures: {},
  view: VIEWS.LIST,
  map: null,
  watchlist: [],
  redirectMessage: undefined,
  propertyAlertName: undefined,
  editAlertUrl: '/property-alerts/edit/',
  manageAlertUrl: '/property-alerts',
  closeMessage: () => {},
  undoDelete: () => {},
  deleteConfirmation: () => {},
  displayMessage: false,
  undo: false,
  messageType: '',
  onAlertClick: () => {},
  onCreateAlert: () => {},
  onManageAlert: () => {},
  onEditAlert: () => {},
  onRemoveAlert: () => {},
  alertId: null,
  alertName: null,
  earlyAccessEnabled: false,
  filterSummary: '',
  listingVisibility: {
    sale: {
      duration: 4,
      unit: 'week'
    },
    rent: {
      duration: 2,
      unit: 'week'
    }
  },
  sendEvent: () => {}
};
export default pageWrapper()(AllhomesListingSearchResults);
export { AllhomesListingSearchResults as BaseAllhomesListingSearchResults };